import { makeAutoObservable } from 'mobx';
import { GET } from '../apis/api';
import { matchSensingData, getDashboardIcon, getSystemIcon, toMeasurementKey } from '../utils/deviceUtils';
import html2canvas from 'html2canvas';

const SYSTEM_METRICS = ['Battery', 'Current', 'System temperature'];

class DeviceStore {
  selectedDevice = null;
  deviceData = {};
  loading = false;
  error = null;

  sensorData = [];
  selectedMeasurement = null;
  dateRange = [];
  availableMeasurements = [];
  measurementUnits = {};

  devices = [];
  deviceSerials = {};
  deviceServiceTypes = {};
  devicesLoading = false;

  measurements = {
    drone: [
      { title: 'Attitude', key: 'attitude' },
      { title: 'Velocity', key: 'velocity' },
      { title: 'IMU', key: 'imu' }
    ]
  };

  constructor() {
    makeAutoObservable(this);
  }

  setSelectedDevice(device) {
    this.selectedDevice = device;
    this.selectedMeasurement = null;
    this.dateRange = [];
    this.availableMeasurements = [];
    this.deviceData = {};
    this.sensorData = [];
    this.error = null;
  }

  setDeviceData(data) { this.deviceData = data; }
  setSensorData(data) { this.sensorData = data; }
  setSelectedMeasurement(measurement) { this.selectedMeasurement = measurement; }
  setDateRange(range) { this.dateRange = range; }
  setLoading(flag) { this.loading = flag; }
  setError(msg) { this.error = msg; }
  setAvailableMeasurements(measurements) { this.availableMeasurements = measurements; }
  setMeasurementUnits(units) { this.measurementUnits = units; }
  setDevices(devices) { this.devices = devices; }
  setDeviceSerials(serials) { this.deviceSerials = serials; }
  setDeviceServiceTypes(types) { this.deviceServiceTypes = types; }
  setDevicesLoading(flag) { this.devicesLoading = flag; }

  getServiceType(deviceName) {
    if (deviceName === 'drone') return 'drone';
    return this.deviceServiceTypes[deviceName] || 'synfield';
  }

  getUnitForSelectedMeasurement() {
    return this.measurementUnits?.[this.selectedMeasurement] || '';
  }

  async fetchDevices() {
    this.setDevicesLoading(true);
    try {
      const res = await GET('/synfield', false, true, 'synfield');
      const list = Array.isArray(res) ? res : (res?.items ?? []);
      const serials = {};
      const serviceTypes = {};
      const devices = list.map(d => {
        serials[d.name] = d.serial_number;
        serviceTypes[d.name] = d.service_type || d.fiware_service || 'synfield';
        return { name: d.name, key: d.name, serial_number: d.serial_number };
      });
      this.setDevices(devices);
      this.setDeviceSerials(serials);
      this.setDeviceServiceTypes(serviceTypes);
    } catch {
      // keep existing devices if fetch fails
    } finally {
      this.setDevicesLoading(false);
    }
  }

  async fetchDeviceData() {
    if (!this.selectedDevice) return;
    this.setLoading(true);
    this.setError(null);

    try {
      if (this.selectedDevice !== 'drone' && this.deviceSerials[this.selectedDevice]) {
        const serial = this.deviceSerials[this.selectedDevice];
        const serviceType = this.getServiceType(this.selectedDevice);

        const [servicesRes, recordsRes] = await Promise.all([
          GET(`/sensing_services?sn=${serial}`, false, true, serviceType),
          GET(`/get_last_n_records/${serial}?last_n=10`, false, true, serviceType),
        ]);

        const sids = servicesRes?.[0]?.sids || [];
        const latestRecord = recordsRes?.items?.find(
          item => Array.isArray(item.synfield_data) && item.synfield_data.length > 0
        );
        const synfieldData = latestRecord?.synfield_data || [];

        const matched = matchSensingData(synfieldData, sids);
        const dash = matched.filter(m => !SYSTEM_METRICS.includes(m.title));
        const sys  = matched.filter(m => SYSTEM_METRICS.includes(m.title));

        this.setDeviceData({
          dashboard: dash.map(i => ({ ...i, icon: getDashboardIcon(i.title) })),
          system:    sys.map(i => ({ ...i, icon: getSystemIcon(i.title) }))
        });

        this.setAvailableMeasurements(
          dash.map(i => ({ title: i.title, key: toMeasurementKey(i.title), id: i.id }))
        );

        const units = {};
        for (const m of dash) {
          const unit = m.unit || (typeof m.value === 'string' && m.value.includes(' ')
            ? m.value.split(' ').pop()
            : '');
          units[toMeasurementKey(m.title)] = unit;
        }
        this.setMeasurementUnits(units);

      } else if (this.selectedDevice === 'drone') {
        const res = await GET('/get_drone', false, true, 'drone');
        const items = res?.items ?? (Array.isArray(res) ? res : []);
        const last = items.slice().reverse().find(d => d?.data?.telemetry);
        const telemetry = last?.data?.telemetry;

        if (!telemetry) {
          this.setError('No telemetry data found for the Drone.');
          this.setDeviceData({ dashboard: [], system: [] });
          return;
        }

        const velocity = telemetry.velocity || {};
        const attitude = telemetry.attitude || {};
        const location = telemetry.location || {};
        const battery  = telemetry.battery  || {};
        const gps      = telemetry.gps      || {};

        this.setDeviceData({
          dashboard: [
            { title: 'Velocity North', value: `${velocity.north?.toFixed(2) ?? '0'} m/s`, icon: getDashboardIcon('Velocity') },
            { title: 'Velocity East',  value: `${velocity.east?.toFixed(2) ?? '0'} m/s`,  icon: getDashboardIcon('Velocity') },
            { title: 'Velocity Down',  value: `${velocity.down?.toFixed(2) ?? '0'} m/s`,  icon: getDashboardIcon('Velocity') },
            { title: 'Altitude',       value: `${location.absolute_altitude?.toFixed(1) ?? '0'} m`, icon: getDashboardIcon('Altitude') },
            { title: 'GPS Signal',     value: gps.fix_type ?? 'Unknown', icon: getDashboardIcon('GPS Signal') },
            { title: 'Flight Mode',    value: telemetry.flight_mode ?? 'N/A', icon: getDashboardIcon('Camera Status') }
          ],
          system: [
            { title: 'Battery Voltage',   value: `${battery.voltage?.toFixed(1) ?? '0'} V`, icon: getSystemIcon('Battery') },
            { title: 'Battery Remaining', value: `${battery.remaining_percent ?? 0}%`,       icon: getSystemIcon('Battery') },
            { title: 'Satellites',        value: `${gps.num_satellites ?? 0}`,               icon: getSystemIcon('Signal') },
            { title: 'Yaw',               value: `${attitude.yaw?.toFixed(2) ?? '0'}°`,      icon: getSystemIcon('System Temperature') }
          ]
        });

        this.setAvailableMeasurements(this.measurements.drone);
        this.setMeasurementUnits({ attitude: '°', velocity: 'm/s', imu: 'm/s²' });
      }
    } catch (e) {
      this.setError('Failed to fetch device data.');
    } finally {
      this.setLoading(false);
    }
  }

  async fetchSensorData(measurement, start, end) {
    this.setLoading(true);
    this.setSensorData([]);
    this.setError(null);

    const measurementObj = this.availableMeasurements.find(m => m.key === measurement);
    const sid = measurementObj?.id;

    try {
      if (this.selectedDevice === 'drone') {
        const formattedStart = start?.toISOString ? start.toISOString() : new Date(start).toISOString();
        const formattedEnd   = end?.toISOString   ? end.toISOString()   : new Date(end).toISOString();

        const res = await GET(
          `/get_drone_start_end/${formattedStart}/${formattedEnd}`,
          false,
          true,
          'drone'
        );

        // Response: { items: [{id, type, data: {timestamp, telemetry: {...}}}] }
        const arr = res?.items ?? (Array.isArray(res) ? res : []);

        const unwrap = (attr) =>
          attr && typeof attr === 'object' && Object.prototype.hasOwnProperty.call(attr, 'value')
            ? attr.value
            : attr;

        const chartData = arr
          .map(entry => {
            const t   = entry?.data?.timestamp || entry?.timestamp || null;
            const tel = entry?.data?.telemetry  || entry?.telemetry || null;
            if (!t || !tel) return null;

            const block = tel[measurement];
            const val   = unwrap(block);
            if (val == null) return null;

            if (typeof val === 'object') {
              if (measurement === 'imu' && val.accelerationFrd) {
                return { date: t, ...unwrap(val.accelerationFrd) };
              }
              return { date: t, ...val };
            }
            return { date: t, value: val };
          })
          .filter(Boolean)
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        if (chartData.length === 0) {
          this.setError('No data available for the selected date range.');
        }
        this.setSensorData(chartData);

      } else {
        const serial      = this.deviceSerials[this.selectedDevice];
        const serviceType = this.getServiceType(this.selectedDevice);
        const startIso    = start?.toISOString ? start.toISOString() : new Date(start).toISOString();
        const endIso      = end?.toISOString   ? end.toISOString()   : new Date(end).toISOString();

        if (!sid) {
          this.setError('Sensor ID not found for the selected measurement.');
          return;
        }

        let allItems = [];
        let offset = 0;
        const pageSize = 100;

        while (true) {
          const response = await GET(
            `/get_synfield_measurements/${serial}/${sid}/${startIso}/${endIso}?limit=${pageSize}&offset=${offset}`,
            false, true, serviceType
          );
          const items = response?.items || [];
          allItems = allItems.concat(items);
          const pagination = response?.pagination;
          // pages = total number of pages; stop when we've fetched all pages
          const totalPages = pagination?.pages ?? 1;
          const currentPage = Math.floor(offset / pageSize) + 1;
          if (!pagination || items.length < pageSize || currentPage >= totalPages) break;
          offset += pageSize;
        }

        const chartData = allItems
          .filter(d => Array.isArray(d) ? d[1] != null : d?.synfield_data?.value != null)
          .map(d => {
            if (Array.isArray(d)) {
              return { date: d[0], value: d[1] };
            }
            return { date: d.synfield_data.ontime, value: d.synfield_data.value };
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        if (chartData.length === 0) {
          this.setError('No data available for the selected date range.');
        }
        this.setSensorData(chartData);
      }
    } catch (e) {
      this.setError('Failed to retrieve sensor data.');
      this.setSensorData([]);
    } finally {
      this.setLoading(false);
    }
  }

  exportSensorDataAsCSV() {
    if (!this.sensorData || this.sensorData.length === 0) return;

    const dataKeys = Object.keys(this.sensorData[0]).filter(k => k !== 'ts');
    const headers = ['timestamp_utc', ...dataKeys.filter(k => k !== 'date')];
    const csvRows = [headers.join(',')];

    for (const row of this.sensorData) {
      const d = new Date(row.date);
      const pad = n => String(n).padStart(2, '0');
      const utc = `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
      const values = dataKeys.filter(k => k !== 'date').map(k => JSON.stringify(row[k] ?? ''));
      csvRows.push([`"${utc}"`, ...values].join(','));
    }

    const measurement = this.selectedMeasurement || 'sensor';
    const device = this.selectedDevice || 'device';
    const [s, e] = this.dateRange || [];
    const range = s && e ? `_${s.format('YYYYMMDD')}_${e.format('YYYYMMDD')}` : '';

    const csv  = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${device}_${measurement}${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  exportChartAsPNG(chartContainerId = 'chart-container') {
    const chart = document.getElementById(chartContainerId);
    if (!chart) return;
    html2canvas(chart).then(canvas => {
      const a    = document.createElement('a');
      a.href     = canvas.toDataURL('image/png');
      a.download = `${this.selectedDevice || 'chart'}_snapshot.png`;
      a.click();
    });
  }
}

export const deviceStore = new DeviceStore();
